import { Users } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import type { AllDataNode } from '@/services/api/graph'
import { cn } from '@/utils/cn'
import { entityColorClass } from './previews/entity-color'

export interface CaseEntitiesCardViewProps {
  /** High-signal entity nodes, ranked by investigation_score. */
  nodes: AllDataNode[]
  /** Total entity count for the case (for the "X total" caption). */
  total: number | null
  isLoading: boolean
  /** Per-type cap for rows shown when a tab is selected. */
  perTypeLimit?: number
}

/**
 * Pure presentation for the report's "Case entities" card: groups the
 * supplied nodes by type, renders a tabbed selector, and lists the
 * highest-volume type first. The connected card in `features/case-report`
 * supplies `nodes` / `total` / `isLoading` from `useAllData`.
 */
export function CaseEntitiesCardView({
  nodes,
  total,
  isLoading,
  perTypeLimit = 12,
}: CaseEntitiesCardViewProps) {
  const { t } = useTranslation()
  const groups = useMemo(() => groupByType(nodes), [nodes])
  const [activeType, setActiveType] = useState<string | null>(null)
  const tabs = groups.map(g => g.label)
  const active = activeType ?? tabs[0] ?? null
  const activeGroup = groups.find(g => g.label === active)

  return (
    <Card className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Users className="size-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">
          {t('cases.entitiesCard.title', 'Case entities')}
        </h3>
        {total !== null && (
          <span className="font-mono text-2xs text-muted-foreground">
            {t('cases.entitiesCard.totalSampled', {
              defaultValue: '{{total}} total · top {{sampled}} sampled',
              total,
              sampled: Math.min(nodes.length, total),
            })}
          </span>
        )}
      </div>

      {isLoading && (
        <p className="text-xs text-muted-foreground">
          {t('cases.entitiesCard.loading', 'Loading entities…')}
        </p>
      )}

      {!isLoading && groups.length === 0 && (
        <p className="text-xs italic text-muted-foreground">
          {t('cases.entitiesCard.empty', 'No entities materialised yet.')}
        </p>
      )}

      {groups.length > 0 && (
        <>
          <div className="flex flex-wrap gap-1">
            {groups.map(group => {
              const isActive = group.label === active
              return (
                <button
                  key={group.label}
                  type="button"
                  onClick={() => setActiveType(group.label)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-2.5 py-2.5 text-xs transition-colors md:py-1',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <span className={cn('h-1.5 w-1.5 rounded-full', dotBgFor(group.label))} />
                  {group.label}
                  <span className="font-mono text-3xs opacity-80">{group.entities.length}</span>
                </button>
              )
            })}
          </div>

          {activeGroup && (
            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {activeGroup.entities.slice(0, perTypeLimit).map(node => (
                <EntityRow key={node.id} node={node} />
              ))}
            </div>
          )}

          {activeGroup && activeGroup.entities.length > perTypeLimit && (
            <p className="text-2xs italic text-muted-foreground">
              {t('cases.entitiesCard.moreNotShown', {
                defaultValue: '+{{count}} more {{label}} entities not shown.',
                count: activeGroup.entities.length - perTypeLimit,
                label: activeGroup.label,
              })}
            </p>
          )}
        </>
      )}
    </Card>
  )
}

function EntityRow({ node }: { node: AllDataNode }) {
  const props = (node.properties ?? {}) as Record<string, unknown>
  const identifier = pickStringProp(props, [
    'e164',
    'msisdn',
    'iban',
    'personal_number',
    'document_number',
    'license_plate',
    'address',
    'txid',
  ])
  return (
    <div className="flex items-baseline gap-2 rounded border border-border/30 bg-background/40 px-2.5 py-1.5">
      <span
        className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dotBgFor(node.type ?? node.labels?.[0]))}
      />
      <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
        {node.name || '—'}
      </span>
      {node.is_suspect && (
        <Badge variant="destructive" className="px-1.5 py-0 text-3xs">
          suspect
        </Badge>
      )}
      {identifier && (
        <span className="max-w-[45%] shrink-0 truncate font-mono text-3xs text-muted-foreground md:max-w-none">
          {identifier}
        </span>
      )}
      {typeof node.investigation_score === 'number' && (
        <span className="shrink-0 font-mono text-3xs text-muted-foreground">
          {node.investigation_score.toFixed(1)}
        </span>
      )}
    </div>
  )
}

interface Group {
  label: string
  entities: AllDataNode[]
}

const SKIP_LABELS = new Set([
  '__Entity__',
  '__KGBuilder__',
  'Chunk',
  'Document',
  'CaseDocument',
  'LogEvent',
  'CaseInsight',
  'CaseAnalysisMeta',
  'Hypothetical',
  'DailySummary',
  'OffchainEvent',
])

function groupByType(nodes: AllDataNode[]): Group[] {
  const groups = new Map<string, AllDataNode[]>()
  for (const node of nodes) {
    const label = pickType(node)
    if (!label || SKIP_LABELS.has(label)) continue
    if (!node.name) continue
    const bucket = groups.get(label) ?? []
    bucket.push(node)
    groups.set(label, bucket)
  }
  return [...groups.entries()]
    .map(([label, entities]) => ({ label, entities }))
    .sort((a, b) => b.entities.length - a.entities.length)
}

function pickType(node: AllDataNode): string | null {
  if (node.type && !SKIP_LABELS.has(node.type)) return node.type
  for (const label of node.labels ?? []) {
    if (!SKIP_LABELS.has(label)) return label
  }
  return null
}

function pickStringProp(props: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = props[key]
    if (typeof value === 'string' && value.trim()) return value
    if (typeof value === 'number') return String(value)
  }
  return null
}

function dotBgFor(label: string | null | undefined): string {
  // Map the text-* class to a matching bg-* utility — both resolve to the same
  // theme token so colour stays in sync with entityColorClass.
  return entityColorClass(label).replace('text-', 'bg-')
}
